import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { TokenSet } from '../../styles/tokens';

const flipIn = keyframes`
  from { transform: rotateY(90deg) scale(0.8); opacity: 0; }
  to { transform: rotateY(0deg) scale(1); opacity: 1; }
`;

const hover = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

interface CardProps {
  tokens: TokenSet;
  selected?: boolean;
  revealed?: boolean;
  selectable?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const sizeMap = {
  sm: { w: 52, h: 76, font: 16 },
  md: { w: 72, h: 104, font: 22 },
  lg: { w: 90, h: 130, font: 28 },
};

export const CardEl = styled.div<CardProps>`
  ${p => {
    const key = (p.size || 'md') as 'sm' | 'md' | 'lg';
    const s = sizeMap[key];
    return css`width: ${s.w}px; height: ${s.h}px; font-size: ${s.font}px;`;
  }}

  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-weight: 800;
  border: 2px solid ${p => p.selected ? p.tokens.primaryLight : p.tokens.cardBorder};
  background: ${p => p.selected ? p.tokens.primary : p.tokens.cardFace};
  color: ${p => p.selected ? '#fff' : p.tokens.text};
  cursor: ${p => p.selectable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  user-select: none;
  position: relative;
  box-shadow: ${p => p.selected
    ? `0 0 0 3px ${p.tokens.primaryLight}40, 0 8px 24px ${p.tokens.shadow}`
    : `0 2px 8px ${p.tokens.shadow}`
  };

  ${p => p.revealed && css`animation: ${flipIn} 0.4s ease;`}

  ${p => p.selectable && !p.selected && css`
    &:hover {
      border-color: ${p.tokens.primaryLight};
      transform: translateY(-4px);
      box-shadow: 0 8px 24px ${p.tokens.shadow};
    }
  `}

  ${p => p.selected && css`
    transform: translateY(-8px);
    animation: ${hover} 2s ease-in-out infinite;
  `}
`;

interface PokerCardProps {
  value: string;
  selected?: boolean;
  revealed?: boolean;
  selectable?: boolean;
  size?: 'sm' | 'md' | 'lg';
  tokens: TokenSet;
  onClick?: () => void;
}

export const PokerCard: React.FC<PokerCardProps> = ({
  value, selected, revealed, selectable, size, tokens, onClick
}) => (
  <CardEl
    tokens={tokens}
    selected={selected}
    revealed={revealed}
    selectable={selectable}
    size={size}
    onClick={onClick}
  >
    {value}
  </CardEl>
);
